import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { inject } from '@angular/core';
import { AlertService } from 'src/app/services/alert.service';
import { UserStoreService } from 'src/app/services/user.store';
import { AuthStoreService } from 'src/app/services/auth.store';
import { Plan, UserStatus, UserPermission, isNormalUseUserStatus } from '../interfaces';

// Comprehensive guard that combines authentication, plan, permission, and subdomain checks
export const FullAccessGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const userStoreService = inject(UserStoreService);
  const authStoreService = inject(AuthStoreService);
  const router = inject(Router);
  const alertService = inject(AlertService);

  // Get route requirements
  const requiredPlans: Plan[] = next.data['plans'] || [];
  const allowedSubdomains: string[] = next.data['allowedSubdomains'] || ['*'];
  const disallowedSubdomains: string[] = next.data['disallowedSubdomains'] || [];
  const requiredPermissions: UserPermission[] = next.data['permissions'] || [];
  const planLabel: string = next.data['planLabel'] || 'Premium';
  const requiresAuth: boolean = next.data['requiresAuth'] !== false; // Default to true unless explicitly set to false
  const softBlockNonPayingCommunity: boolean = next.data['softBlockNonPayingCommunity'] || false;
  const requiresUserPaysCommunity: boolean = next.data['requiresUserPaysCommunity'] || false;

  // console.log('* Guard Param requiresAuth: ', requiresAuth);
  // console.log('* Guard Param requiredPlans: ', requiredPlans);
  // console.log('* Guard Param requiredPermissions: ', requiredPermissions);
  // console.log('* Guard Param softBlockNonPayingCommunity: ', softBlockNonPayingCommunity);

  // Get current subdomain
  const currentSubdomain = userStoreService.getSubdomain();
  console.log('Entered FullAccessGuard, currentSubdomain: ', currentSubdomain);

  // Subdomain allow/deny checks (fastest checks)
  if (!allowedSubdomains.includes('*') && !allowedSubdomains.includes(currentSubdomain)) {
    console.log(`Subdomain access denied. Current: "${currentSubdomain}", Allowed: [${allowedSubdomains.join(', ')}]`);
    return router.navigate(['/']);
  }
  if (disallowedSubdomains.length > 0 && disallowedSubdomains.includes(currentSubdomain)) {
    console.log(`Subdomain access denied. Current: "${currentSubdomain}", Disallowed: [${disallowedSubdomains.join(', ')}]`);
    return router.navigate(['/']);
  }

  // If this route doesn't require authentication (like signin/signup),
  // and no plans, permissions, or soft community checks are required, allow access after subdomain check.
  // Still await the boot-time community fetch to settle - guest components like signup-short read
  // authStoreService.community() synchronously on activation (e.g. to block signup for a
  // community missing a required field), and without this wait a fresh page load can race that
  // fetch and read the emptyCommunity placeholder instead (see 2026-08-11 investigation, and the
  // same wait a few lines below for authenticated routes).
  if (!requiresAuth && requiredPlans.length === 0 && requiredPermissions.length === 0 && !softBlockNonPayingCommunity && !requiresUserPaysCommunity) {
    console.log('Route allows guest access, subdomain check passed');
    return authStoreService.getCommunityStateSettledAsync().then(() => true);
  }

  // Authentication and permission checks (async) - only if required.
  // Also wait for the boot-time community fetch to settle — downstream checks below
  // (softBlockNonPayingCommunity, requiresUserPaysCommunity) and the routed component
  // itself commonly read authStoreService.community()/userStoreService.community()
  // synchronously on activation; without this wait a hard refresh can race that fetch
  // and read the emptyCommunity placeholder instead (see 2026-08-11 investigation).
  return Promise.all([
    userStoreService.getUserStatusAsync(),
    authStoreService.getCommunityStateSettledAsync()
  ]).then(([userStatus]) => {
    console.log('Guard checking if user is activated, userStatus: ', userStatus);

    // PASSWORD_RESET is set while a self-service reset (Settings "Change
    // password", or the anonymous forgot-password flow) is pending. It only
    // exists to block a *new* sign-in with the now-superseded password
    // (see signin.component.ts) — it was never meant to invalidate an
    // already-established, still-valid session, so it counts as
    // authenticated here too.
    const isAuthenticated = isNormalUseUserStatus(userStatus) || userStatus === UserStatus.PASSWORD_RESET;

    // User must be activated for protected routes
    if (!isAuthenticated && requiresAuth) {
      console.log('Guard routing to home');
      return router.navigate(['/home']);
    }

    // Soft-block: any user (anonymous or authenticated) on a community whose userPays === false.
    // Uses the community signal from AuthStoreService, which is resolved from the subdomain at app init.
    if (softBlockNonPayingCommunity) {
      const community = authStoreService.community();
      if ((community.id ?? 0) > 2 && community.userPays === false) {
        console.log('Guard blocking teams access: community does not allow user payments');
        return router.navigate(['/home']);
      }
    }

    // Hard-block community end users (communityId > 2) whose community has userPays === false
    if (requiresUserPaysCommunity) {
      const user = userStoreService.user();
      if (user && user.communityId !== undefined && user.communityId > 2 && user.Community?.userPays === false) {
        console.log('Guard blocking plans access: community does not allow user payments');
        return router.navigate(['/home']);
      }
    }

    // Unauthenticated users that reached here (requiresAuth: false) need no further plan/permission checks
    if (!isAuthenticated) {
      return true;
    }

    // Permission check
    if (requiredPermissions.length > 0) {
      return userStoreService.getUserPermissionAsync().then(userPermission => {
        if (!requiredPermissions.includes(userPermission)) {
          console.log('Full-access guard failed on permissions: ', userPermission, requiredPermissions);
          alertService.alert.set({
            visible: true,
            autoClose: true,
            text: 'You do not have permission to access this resource.',
            severity: 4,
            upgradeLink: false,
          });
          return router.navigate(['/home']);
        }

        // Plan check (if permission check passed)
        if (requiredPlans.length > 0) {
          console.log('Checking PLANS guard with required permissions: ', requiredPlans);
          return userStoreService.getUserPlanAsync().then(userPlan => {
            console.log('Retreived userPlan in guard with required permissions: ', userPlan);
            if (!requiredPlans.includes(userPlan)) {
              alertService.alert.set({
                visible: true,
                autoClose: true,
                text: `This feature requires ${planLabel} plan access.`,
                severity: 4,
                upgradeLink: true,
              });
              return router.navigate(['/plans']);
            }
            
            // All checks passed
            return true;
          });
        }
        
        // Permission check passed, no plan requirement
        return true;
      });
    }

    // No permission requirement, check plan only
    if (requiredPlans.length > 0) {
      console.log('Checking PLANS guard: ', requiredPlans);
      return userStoreService.getUserPlanAsync().then(userPlan => {
        console.log('Retreived userPlan in guard: ', userPlan);
        if (!requiredPlans.includes(userPlan)) {
          console.log('Raising feature alert in guard');
          alertService.alert.set({
            visible: true,
            autoClose: true,
            text: `This feature requires ${planLabel} plan access.`,
            severity: 4,
            upgradeLink: true,
          });
          return router.navigate(['/plans']);
        }
        
        // Plan check passed
        console.log('Full-access guard passed successfully');
        return true;
      });
    }

    // Only authentication required (user is activated)
    return true;

  }).catch(error => {
    console.error('Error in FullAccessGuard:', error);
    return router.navigate(['/home']);
  });
};

// UPDATED USAGE EXAMPLES:

// Route that allows guest access (signin/signup) - only checks subdomain
/*
{
  path: 'signin',
  component: SigninComponent,
  canActivate: [FullAccessGuard],
  data: {
    allowedSubdomains: ['auth'],
    requiresAuth: false  // Key addition - allows guest access
  }
}

// Route that allows guest access but also checks subdomain
{
  path: 'signup',
  component: SignupComponent,
  canActivate: [FullAccessGuard],
  data: {
    allowedSubdomains: ['auth', 'biz', 'fans'],
    requiresAuth: false  // Key addition - allows guest access
  }
}

// Public routes (no auth or subdomain restrictions)
{
  path: 'home',
  component: HomeComponent,
  canActivate: [FullAccessGuard],
  data: {
    requiresAuth: false,
    allowedSubdomains: ['*']  // or omit this line as '*' is default
  }
}

// Protected route with comprehensive access control
{
  path: 'super-admin',
  component: SuperAdminComponent,
  canActivate: [FullAccessGuard],
  data: {
    plans: [Plan.BUSINESS_PREMIUM],
    allowedSubdomains: ['*'],
    permissions: [UserPermission.SYS_ADMIN],
    planLabel: 'Business Premium'
    // requiresAuth defaults to true
  }
}

// Route with authentication only (just checks if user is activated)
{
  path: 'user-dashboard',
  component: UserDashboardComponent,
  canActivate: [FullAccessGuard],
  data: {} // requiresAuth defaults to true, other checks skipped
}
*/